import type { Spec } from "@json-render/react";

import { computeEMAs } from "@utils/technicalAnalysisCalc";
import type { JsonRenderSchemaAssetType } from "@components/Chat/JsonRenderComponents/type";
import type {
  ChartAnnotationPoint,
  EarningsMetricRow,
  EarningsResultTone,
  OhlcCandlePoint,
  PriceChangeDirection,
  RsiStatusTone,
  SignalCardAssetProps,
} from "@components/Chat/JsonRenderComponents/signalCards/types";
import {
  MOVES_SIGNAL_VIZ_ELEMENT_ID,
  type MovesVizEventType,
} from "./movesSignalCardConstants";

export type MovesMetrics = Record<string, unknown>;

export type MovesSource = {
  ticker?: string | string[];
  theme?: string | string[];
};

const asNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

const formatSignedPercent = (value: number, digits = 2): string => {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(digits)}%`;
};

const formatUsdPrice = (value: number): string => {
  if (value >= 1000) {
    return `$${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  }
  return `$${value.toFixed(2)}`;
};

const formatVolumeMultiplier = (ratio: number): string =>
  `${ratio.toFixed(1)}x`;

const resolveChangeDirection = (value: number): PriceChangeDirection => {
  if (value > 0) {
    return "up";
  }
  if (value < 0) {
    return "down";
  }
  return "flat";
};

const inferAssetType = (
  metrics: MovesMetrics,
  source?: MovesSource,
): JsonRenderSchemaAssetType => {
  const businessType = asString(metrics.business_type)?.toLowerCase();
  if (businessType?.includes("token") || businessType?.includes("crypto")) {
    return "crypto";
  }
  if ((source?.theme?.length ?? 0) > 0 && !source?.ticker?.[0]) {
    return "stock";
  }
  return "stock";
};

const resolveAssetBase = (
  metrics: MovesMetrics,
  source?: MovesSource,
): SignalCardAssetProps => {
  const assetSymbol =
    source?.ticker?.[0] ??
    asString(metrics.from_theme_name) ??
    asString(metrics.to_theme_name) ??
    "—";

  return {
    assetSymbol,
    assetLogo: asString(metrics.ticker_icon),
    type: inferAssetType(metrics, source),
  };
};

/** Wire: `{ open, high, low, close, volume?, time? }[]` per design §4.1 */
const mapOhlcvToCandles = (value: unknown): OhlcCandlePoint[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const parsed = value
    .map((item) => {
      if (!item || typeof item !== "object") {
        return null;
      }
      const row = item as Record<string, unknown>;
      const open = asNumber(row.open);
      const high = asNumber(row.high);
      const low = asNumber(row.low);
      const close = asNumber(row.close);
      if (
        open === undefined ||
        high === undefined ||
        low === undefined ||
        close === undefined
      ) {
        return null;
      }
      const time = asNumber(row.time);
      return {
        open,
        high,
        low,
        close,
        ...(time !== undefined ? { time } : {}),
      };
    })
    .filter(
      (
        item,
      ): item is OhlcCandlePoint & {
        time?: number;
      } => item !== null,
    );

  if (parsed.some((row) => row.time !== undefined)) {
    parsed.sort((a, b) => (a.time ?? 0) - (b.time ?? 0));
  }

  return parsed.map(({ open, high, low, close }) => ({
    open,
    high,
    low,
    close,
  }));
};

const mapSparklineToChartPoints = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => {
      if (!item || typeof item !== "object") {
        return undefined;
      }
      return asNumber((item as Record<string, unknown>).price);
    })
    .filter((item): item is number => item !== undefined);
};

/** 在序列中找最接近 target 的点；等距时取更靠后的点（更近发生时刻） */
const findClosestPointIndexPreferLast = (
  points: number[],
  target: number,
): number => {
  if (points.length === 0) {
    return 0;
  }

  let bestIndex = 0;
  let bestDiff = Infinity;

  for (let i = 0; i < points.length; i++) {
    const diff = Math.abs(points[i]! - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = i;
    } else if (diff === bestDiff) {
      bestIndex = i;
    }
  }

  return bestIndex;
};

const resolveSparkline52wAnnotation = (
  chartPoints: number[],
  metrics: MovesMetrics,
): ChartAnnotationPoint | undefined => {
  if (chartPoints.length === 0) {
    return undefined;
  }

  const isHighExtreme = asNumber(metrics.price_52w_high) !== undefined;
  const isLowExtreme = asNumber(metrics.price_52w_low) !== undefined;

  if (isHighExtreme) {
    const target =
      asNumber(metrics.high_52w) ?? asNumber(metrics.price_52w_high);
    if (target === undefined) {
      return undefined;
    }
    return {
      pointIndex: findClosestPointIndexPreferLast(chartPoints, target),
      label: "52w high",
    };
  }

  if (isLowExtreme) {
    const target = asNumber(metrics.low_52w) ?? asNumber(metrics.price_52w_low);
    if (target === undefined) {
      return undefined;
    }
    return {
      pointIndex: findClosestPointIndexPreferLast(chartPoints, target),
      label: "52w low",
    };
  }

  return undefined;
};

type EmaCrossType = "golden_cross" | "death_cross";

const mapWireEmaSeries = (
  value: unknown,
): Partial<Record<7 | 14, number[]>> => {
  if (!Array.isArray(value)) {
    return {};
  }

  const out: Partial<Record<7 | 14, number[]>> = {};

  for (const item of value) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const row = item as Record<string, unknown>;
    const period = asNumber(row.timePeriod);
    if (period !== 7 && period !== 14) {
      continue;
    }
    if (!Array.isArray(row.values)) {
      continue;
    }
    const values = row.values
      .map((v) => asNumber(v))
      .filter((v): v is number => v !== undefined);
    if (values.length > 0) {
      out[period] = values;
    }
  }

  return out;
};

const findLatestEmaCrossIndex = (
  ema7: number[],
  ema14: number[],
  crossType: EmaCrossType,
): number | undefined => {
  const len = Math.min(ema7.length, ema14.length);
  if (len < 2) {
    return undefined;
  }

  for (let i = len - 1; i >= 1; i--) {
    const fastPrev = ema7[i - 1]!;
    const slowPrev = ema14[i - 1]!;
    const fastNow = ema7[i]!;
    const slowNow = ema14[i]!;

    if (
      !Number.isFinite(fastPrev) ||
      !Number.isFinite(slowPrev) ||
      !Number.isFinite(fastNow) ||
      !Number.isFinite(slowNow)
    ) {
      continue;
    }

    if (
      crossType === "golden_cross" &&
      fastPrev < slowPrev &&
      fastNow > slowNow
    ) {
      return i;
    }
    if (
      crossType === "death_cross" &&
      fastPrev > slowPrev &&
      fastNow < slowNow
    ) {
      return i;
    }
  }

  return undefined;
};

const resolveEmaCrossAnnotation = (
  metrics: MovesMetrics,
  candles: OhlcCandlePoint[],
): ChartAnnotationPoint | undefined => {
  const crossType = asString(metrics.ema_cross_type);
  if (crossType !== "golden_cross" && crossType !== "death_cross") {
    return undefined;
  }

  const wireEma = mapWireEmaSeries(metrics.ema);
  let crossIndex =
    wireEma[7] && wireEma[14]
      ? findLatestEmaCrossIndex(wireEma[7], wireEma[14], crossType)
      : undefined;

  if (crossIndex === undefined && candles.length > 0) {
    const closes = candles.map((c) => c.close);
    const emas = computeEMAs(closes, [7, 14]);
    crossIndex = findLatestEmaCrossIndex(emas[7]!, emas[14]!, crossType);
  }

  if (crossIndex === undefined) {
    return undefined;
  }

  return {
    pointIndex: crossIndex,
    label: crossType === "golden_cross" ? "Golden cross" : "Death cross",
  };
};

const parseTranscriptsToBullets = (transcripts: string): string[] =>
  transcripts
    .split("\n")
    .map((line) => line.replace(/^[-*•]\s*/, "").trim())
    .filter(Boolean);

const resolveEarningsResult = (surprisePct?: number): EarningsResultTone => {
  if (surprisePct === undefined) {
    return "inline";
  }
  if (surprisePct > 0.5) {
    return "beat";
  }
  if (surprisePct < -0.5) {
    return "miss";
  }
  return "inline";
};

const formatCompactUsd = (value: number): string => {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(2)}B`;
  }
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`;
  }
  return formatUsdPrice(value);
};

const buildVolumeComparisonProps = (
  asset: SignalCardAssetProps,
  metrics: MovesMetrics,
  ratioKey: "volume_ratio_vs_avg" | "volume_ratio_vs_20d",
) => {
  const ratio = asNumber(metrics[ratioKey]) ?? 1;
  const normalizedMax = Math.max(ratio, 1);

  return {
    type: "AssetVolumeComparisonCard" as const,
    props: {
      ...asset,
      volumeMultiplier: formatVolumeMultiplier(ratio),
      currentVolumeRatio: Math.min(1, ratio / normalizedMax),
      avgVolumeRatio: Math.min(1, 1 / normalizedMax),
    },
    children: [] as string[],
  };
};

const buildPriceSparklineProps = (
  asset: SignalCardAssetProps,
  metrics: MovesMetrics,
) => {
  const price = asNumber(metrics.price) ?? 0;
  const changePct = asNumber(metrics.change_pct_1d) ?? 0;
  const chartPoints = mapSparklineToChartPoints(metrics.sparkline);
  const annotation = resolveSparkline52wAnnotation(chartPoints, metrics);

  return {
    type: "AssetPriceSparklineCard" as const,
    props: {
      ...asset,
      price: formatUsdPrice(price),
      priceChange: formatSignedPercent(changePct, 2).replace("%", ""),
      priceChangePercent: formatSignedPercent(changePct, 2),
      changeDirection: resolveChangeDirection(changePct),
      chartPoints,
      ...(annotation ? { annotation } : {}),
    },
    children: [] as string[],
  };
};

const buildEarningsMetrics = (metrics: MovesMetrics): EarningsMetricRow[] => {
  const rows: EarningsMetricRow[] = [];

  const epsActual = asNumber(metrics.eps_actual);
  const epsEstimate = asNumber(metrics.eps_estimate);
  if (epsActual !== undefined && epsEstimate !== undefined) {
    rows.push({
      metricKey: "eps",
      actualValue: epsActual.toFixed(2),
      expectedValue: epsEstimate.toFixed(2),
      result: resolveEarningsResult(asNumber(metrics.eps_surprise_pct)),
    });
  }

  const revenueActual = asNumber(metrics.revenue_actual);
  const revenueEstimate = asNumber(metrics.revenue_estimate);
  if (revenueActual !== undefined && revenueEstimate !== undefined) {
    rows.push({
      metricKey: "revenue",
      actualValue: formatCompactUsd(revenueActual),
      expectedValue: formatCompactUsd(revenueEstimate),
      result: resolveEarningsResult(asNumber(metrics.revenue_surprise_pct)),
    });
  }

  return rows;
};

export const mapMetricsToVizElement = (
  eventType: MovesVizEventType,
  metrics: MovesMetrics,
  source?: MovesSource,
): Spec["elements"][string] | null => {
  const asset = resolveAssetBase(metrics, source);

  switch (eventType) {
    case "price_52w_extreme":
      return buildPriceSparklineProps(asset, metrics);

    case "volume_surge":
      return buildVolumeComparisonProps(asset, metrics, "volume_ratio_vs_avg");

    case "volume_anomaly":
    case "volume_shrink":
      return buildVolumeComparisonProps(asset, metrics, "volume_ratio_vs_20d");

    case "ta_rsi_extreme": {
      const rsiValue = asNumber(metrics.rsi) ?? 50;
      const statusTone: RsiStatusTone | undefined = metrics.rsi_overbought
        ? "overbought"
        : metrics.rsi_oversold
          ? "oversold"
          : undefined;
      return {
        type: "AssetRsiGaugeCard",
        props: {
          ...asset,
          rsiValue,
          ...(statusTone ? { statusTone } : {}),
        },
        children: [],
      };
    }

    case "ta_kdj_extreme": {
      const kdjValue = asNumber(metrics.kdj_value) ?? 50;
      const statusTone: RsiStatusTone | undefined = metrics.kdj_overbought
        ? "overbought"
        : metrics.kdj_oversold
          ? "oversold"
          : undefined;
      return {
        type: "AssetKdjGaugeCard",
        props: {
          ...asset,
          kdjValue,
          ...(statusTone ? { statusTone } : {}),
        },
        children: [],
      };
    }

    case "ta_macd_cross":
      return {
        type: "AssetMacdMiniChartCard",
        props: {
          ...asset,
          candles: mapOhlcvToCandles(metrics.ohlcv),
        },
        children: [],
      };

    case "ta_ema_cross": {
      const candles = mapOhlcvToCandles(metrics.ohlcv);
      const annotation = resolveEmaCrossAnnotation(metrics, candles);

      return {
        type: "AssetEmaCandleChartCard",
        props: {
          ...asset,
          candles,
          ...(annotation ? { annotation } : {}),
        },
        children: [],
      };
    }

    case "ta_outlook_bullish":
    case "ta_outlook_bearish":
      return {
        type: "AssetSentimentRatioCard",
        props: {
          ...asset,
          bullishCount: asNumber(metrics.bullish_signal_count) ?? 0,
          bearishCount: asNumber(metrics.bearish_signal_count) ?? 0,
        },
        children: [],
      };

    case "sector_rotation": {
      const fromPct = asNumber(metrics.outflow_volume_pct_1d) ?? 0;
      const toPct = asNumber(metrics.inflow_volume_pct_1d) ?? 0;
      return {
        type: "AssetSectorShiftCard",
        props: {
          ...asset,
          fromSectorName:
            asString(metrics.from_theme_name) ??
            asString(metrics.from_theme) ??
            "From",
          fromSectorChange: formatSignedPercent(fromPct, 1),
          toSectorName:
            asString(metrics.to_theme_name) ??
            asString(metrics.to_theme) ??
            "To",
          toSectorChange: formatSignedPercent(toPct, 1),
        },
        children: [],
      };
    }

    case "earnings_flash":
      return {
        type: "AssetEarningsActualExpectedCard",
        props: {
          ...asset,
          metrics: buildEarningsMetrics(metrics),
        },
        children: [],
      };

    case "earnings_call_ready": {
      const title = asString(metrics.title) ?? "Earnings highlights";
      const transcripts = asString(metrics.transcripts) ?? "";
      return {
        type: "AssetInsightSummaryCard",
        props: {
          ...asset,
          title,
          bullets: parseTranscriptsToBullets(transcripts),
        },
        children: [],
      };
    }

    case "ai_pick_published": {
      const entryPrice = asNumber(metrics.entry_price);
      const stopLoss = asNumber(metrics.stop_loss_price);
      const expectedReturn = asNumber(metrics.expected_return_pct) ?? 0;
      const currentReturn = asNumber(metrics.current_return_pct);

      return {
        type: "AssetTradePotentialCard",
        props: {
          ...asset,
          potentialPercent: formatSignedPercent(expectedReturn, 1),
          entryPrice:
            entryPrice !== undefined ? formatUsdPrice(entryPrice) : "—",
          stopLossPrice:
            stopLoss !== undefined ? formatUsdPrice(stopLoss) : "—",
          currentReturn:
            currentReturn !== undefined
              ? formatSignedPercent(currentReturn, 1)
              : "—",
          direction: resolveChangeDirection(expectedReturn),
        },
        children: [],
      };
    }

    default:
      return null;
  }
};

export const buildVizSpecFragment = (
  eventType: MovesVizEventType,
  metrics: MovesMetrics,
  source?: MovesSource,
): Pick<Spec, "elements"> | null => {
  const vizElement = mapMetricsToVizElement(eventType, metrics, source);
  if (!vizElement) {
    return null;
  }

  return {
    elements: {
      [MOVES_SIGNAL_VIZ_ELEMENT_ID]: vizElement,
    },
  };
};
